import React from 'react';

import { Select } from '@aline/common';
import { makeVar } from '@apollo/client';
import { useReactiveVar } from '@apollo/client/react';
import { Typography } from '@mui/material';
import { AlineButton } from 'src/components';
import { MobileBottomSheet } from 'src/editor/mobile/MobileBottomSheet';
import { useMobileSheetActionButtonStyles } from 'src/editor/mobile/signing/mobile-sheet-action-button';
import { useWalkthrough } from 'src/editor/toolbars';
import { useOnChange } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

/**
 * Flipped from inside a field sheet (via MobileSheetActionFooter) and from the
 * FAB (MobileWalkthroughNextButton), so the var has to live outside both.
 */
export const mobileFinishSheetOpenVar = makeVar<boolean>(false);

const useStyles = makeStyles()(theme => ({
  actions: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    // Extra height so the final sheet doesn't sit so low on screen.
    paddingBottom: 20,
  },
  description: {
    color: theme.palette.text.secondary,
    fontSize: 14,
    lineHeight: 1.4,
  },
  heading: {
    fontSize: 24,
    fontWeight: 700,
    marginBottom: theme.spacing(0.5),
  },
}));

export function MobileFinishSheet(): JSX.Element | null {
  const { classes } = useStyles();
  const { classes: actionClasses } = useMobileSheetActionButtonStyles();
  const finishSheetOpen = useReactiveVar(mobileFinishSheetOpenVar);
  const { advanceWalkthrough, isFieldWalkthroughFinished, submitting } = useWalkthrough();

  /**
   * If the user un-finishes the walkthrough by editing a field again, drop
   * the open finish sheet — it's no longer the right prompt.
   */
  useOnChange(() => {
    if (!isFieldWalkthroughFinished && finishSheetOpen) mobileFinishSheetOpenVar(false);
  }, [isFieldWalkthroughFinished, finishSheetOpen]);

  if (!finishSheetOpen) return null;

  return (
    <MobileBottomSheet onDismiss={() => mobileFinishSheetOpenVar(false)}>
      <div className={classes.body}>
        <div>
          <Typography className={classes.heading}>Are you sure?</Typography>
          <Typography className={classes.description}>
            You&apos;ve completed all required fields. Review the document one last time before
            submitting your signature, or finish signing now.
          </Typography>
        </div>
        <div className={classes.actions}>
          <AlineButton
            fullWidth
            buttonClassName={actionClasses.button}
            color="primary"
            variant="outlined"
            onClick={() => mobileFinishSheetOpenVar(false)}
          >
            Continue Reviewing
          </AlineButton>
          <AlineButton
            fullWidth
            buttonClassName={actionClasses.button}
            color="primary"
            data-cy={Select.document.editor.walkthrough.finishSigningButton}
            loading={submitting}
            variant="contained"
            onClick={e => advanceWalkthrough(e, { canExecuteFinish: true })}
          >
            Finish Signing
          </AlineButton>
        </div>
      </div>
    </MobileBottomSheet>
  );
}
